import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, User, Home, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { Button } from '../components/ui/Button';
import { useToastStore } from '../store/useToastStore';
import { supabase } from '../lib/supabase';

export default function Login() {
  const navigate = useNavigate();
  const { setUser, error, loading, isAuthenticated } = useAuthStore();
  const { showToast } = useToastStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Debug useEffect to check URL parameters when component loads
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect');
    const valuationId = urlParams.get('valuationId');

    console.log('Login page loaded with URL params:', {
      redirectPath,
      valuationId,
      fullUrl: window.location.href
    });

    // Check if localStorage already has values
    console.log('Current localStorage values:', {
      auth_redirect_path: localStorage.getItem('auth_redirect_path'),
      pending_valuation_id: localStorage.getItem('pending_valuation_id')
    });
  }, []);

  // If the user becomes authenticated while on this page (either an existing
  // session was restored by AuthInitializer, or signInWithEmail succeeded
  // server-side even though its promise hung), redirect them out.
  useEffect(() => {
    if (!isAuthenticated) return;
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect') || '/';
    console.log('Login: already authenticated, redirecting to', redirectPath);
    navigate(redirectPath, { replace: true });
  }, [isAuthenticated, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic form validation
    if (!email.trim()) {
      showToast('Please enter your email address', 'error');
      return;
    }

    if (!password) {
      showToast('Please enter your password', 'error');
      return;
    }
    
    try {
      console.log('Login attempt started for:', email);
      const response = await supabase.auth.signInWithEmail(email, password);

      // Check for errors in the response first
      if (response?.error) {
        console.error('Login error from Supabase:', response.error);

        let errorMessage = 'Login failed. Please check your credentials.';

        if (response.error.message.includes('Invalid login credentials')) {
          errorMessage = 'Incorrect email or password. If you just signed up, please check your email to confirm your account first.';
        } else if (response.error.message.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and click the confirmation link to verify your account before logging in.';
        } else if (response.error.message.includes('Email link is invalid') || response.error.message.includes('has expired')) {
          errorMessage = 'Your confirmation link has expired. Please contact support or sign up again.';
        } else {
          errorMessage = response.error.message;
        }

        showToast(errorMessage, 'error');
        return;
      }

      if (response?.data?.user) {
        console.log('Login successful, fetching session...');
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token || '';

        if (!token) {
          console.error('No access token found in session');
          showToast('Authentication error. Please try again.', 'error');
          return;
        }

        setUser({
          id: response.data.user.id,
          user_id: response.data.user.id,
          full_name: response.data.user.user_metadata?.full_name || null,
          name: response.data.user.user_metadata?.name || null,
          email: response.data.user.email || null,
          avatar_url: response.data.user.user_metadata?.avatar_url || null,
          company_name: response.data.user.user_metadata?.company_name || null,
          subscription_tier: 'free',
          subscription_ends_at: null,
          created_at: response.data.user.created_at,
          updated_at: response.data.user.last_sign_in_at || response.data.user.created_at
        }, token);

        // Redirect to homepage
        navigate('/');
      } else {
        // This should rarely happen due to error handling in signInWithEmail
        console.error('Login failed: User data not returned');
        showToast('Incorrect email or password. If you just signed up, please check your email to confirm your account first.', 'error');
      }
    } catch (err: any) {
      console.error('Login error details:', err);

      // Handle specific Supabase auth errors
      let errorMessage = 'Failed to sign in';

      if (err.message) {
        if (err.message.includes('Invalid login credentials')) {
          errorMessage = 'Incorrect email or password. If you just signed up, please check your email to confirm your account first.';
        } else if (err.message.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and click the confirmation link to verify your account before logging in.';
        } else if (err.message.includes('Email link is invalid or has expired')) {
          errorMessage = 'Your confirmation link has expired. Please contact support or sign up again.';
        } else {
          // Use the error message from the error object
          errorMessage = err.message;
        }
      }

      showToast(errorMessage, 'error');
    }
  };

  /**
   * Handle Google SSO login
   */
  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      
      // Get URL parameters for redirect path and valuation ID
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get('redirect');
      const valuationId = urlParams.get('valuationId');

      // Log debug information
      console.log('Login: Starting Google login with params:', {
        redirectPath,
        valuationId
      });

      // Use state parameter to preserve data across OAuth flow instead of localStorage
      await supabase.auth.signInWithGoogle({
        redirectPath: redirectPath || undefined,
        valuationId: valuationId || undefined
      });

      // In case the redirect doesn't happen automatically
      setIsLoading(false);
    } catch (error) {
      console.error('Google login error:', error);
      showToast('Unable to login with Google. Please try again.', 'error');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
      <Link
        to="/"
        className="absolute top-8 left-8 flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
      >
        <Home className="w-5 h-5" />
        <span>Return Home</span>
      </Link>

      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-2">Sign In to Your Account</h2>
          <p className="text-gray-600">Welcome back! Access your dashboard and valuations</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleEmailLogin} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Link
                to="/reset-password"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Forgot Password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            loading={loading}
          >
            Sign In with Email
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleLogin}
        >
          <img
            src="https://www.google.com/favicon.ico"
            alt="Google"
            className="w-5 h-5 mr-2"
          />
          Sign in with Google
        </Button>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}